import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';

// Public pages
import LandingPage from './pages/public/LandingPage';
import About from './pages/public/About';
import Priser from './pages/public/Priser';
import Personvern from './pages/public/Personvern';
import Vilkar from './pages/public/Vilkar';
import Kontakt from './pages/public/Kontakt';
import FAQ from './pages/public/FAQ';
import VersionHistory from './pages/public/VersionHistory';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmail from './pages/auth/VerifyEmail';
import VerifyOrganizerEmail from './pages/auth/VerifyOrganizerEmail';

// 404 page
import NotFound from './pages/NotFound';

// Participant pages
import ParticipantDashboard from './pages/participant/Dashboard';
import MyEvents from './pages/participant/MyEvents';
import EventDetails from './pages/participant/EventDetails';
import NextStop from './pages/participant/NextStop';
import Interactions from './pages/participant/Interactions';
import JoinEvent from './pages/participant/JoinEvent';
import Profile from './pages/participant/Profile';
import BecomeOrganizer from './pages/participant/BecomeOrganizer';
import { EventFeedback } from './pages/participant/EventFeedback';

// Organizer pages
import OrganizerDashboard from './pages/organizer/Dashboard';
import CreateEvent from './pages/organizer/CreateEvent';
import ManageEvent from './pages/organizer/ManageEvent';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import EditUser from './pages/admin/EditUser';
import OrganizerManagement from './pages/admin/OrganizerManagement';
import Settings from './pages/admin/Settings';

// Components
import PrivateRoute from './components/PrivateRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

import './App.css';

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <ScrollToTop />
          <div className="app">
            <Header />
            <main className="app-main">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/om" element={<About />} />
                <Route path="/priser" element={<Priser />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/personvern" element={<Personvern />} />
                <Route path="/vilkar" element={<Vilkar />} />
                <Route path="/kontakt" element={<Kontakt />} />
                <Route path="/versjonshistorikk" element={<VersionHistory />} />
                <Route path="/logg-inn" element={<LoginPage />} />
                <Route path="/opprett-konto" element={<RegisterPage />} />
                <Route path="/glemt-passord" element={<ForgotPasswordPage />} />
                <Route path="/tilbakestill-passord" element={<ResetPasswordPage />} />
                <Route path="/verifiser-epost" element={<VerifyEmail />} />
                <Route path="/verify-organizer-email/:token" element={<VerifyOrganizerEmail />} />

                {/* Participant routes */}
                <Route
                  path="/deltaker/dashboard"
                  element={
                    <PrivateRoute roles={['participant']}>
                      <ParticipantDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/deltaker/mine-fester"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <MyEvents />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/bli-med"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <JoinEvent />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/deltaker/arrangement/:id"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <EventDetails />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/deltaker/neste-stopp"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <NextStop />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/deltaker/hvem-jeg-motte"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <Interactions />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/deltaker/profil"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <Profile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/bli-arrangor"
                  element={
                    <PrivateRoute roles={['participant']}>
                      <BecomeOrganizer />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/feedback/:eventId"
                  element={
                    <PrivateRoute roles={['participant', 'organizer', 'admin']}>
                      <EventFeedback />
                    </PrivateRoute>
                  }
                />

                {/* Organizer routes */}
                <Route
                  path="/arrangor/dashboard"
                  element={
                    <PrivateRoute roles={['organizer', 'admin']}>
                      <OrganizerDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/arrangor/opprett"
                  element={
                    <PrivateRoute roles={['organizer', 'admin']}>
                      <CreateEvent />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/arrangor/arrangement/:id"
                  element={
                    <PrivateRoute roles={['organizer', 'admin']}>
                      <ManageEvent />
                    </PrivateRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <PrivateRoute roles={['admin']}>
                      <AdminDashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/brukere"
                  element={
                    <PrivateRoute roles={['admin']}>
                      <UserManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/users/:id"
                  element={
                    <PrivateRoute roles={['admin']}>
                      <EditUser />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/organisatorer"
                  element={
                    <PrivateRoute roles={['admin']}>
                      <OrganizerManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/innstillinger"
                  element={
                    <PrivateRoute roles={['admin']}>
                      <Settings />
                    </PrivateRoute>
                  }
                />
                
                {/* 404 - Catch all unmatched routes */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
